import { cloneRecord, isRecord, stringField } from '../core/record';
import type { ChatItem } from '../core/types';
import { isInvalidEchoMessage } from '../echo/invalidMessage';
import { ECHO_PLACEHOLDER_AUTHOR_FLAG } from '../echo/project';
import type { EntitySnapshotMaps } from './entityBundle';
import { enrichRendererReactions, profileSnapshot, richdownEntitySnapshots } from './rendererRowEnrichment';

export type MessageListRendererRow = Record<string, unknown>;
export type RendererRowBuildObserver = (item: ChatItem) => void;

/** Caches legacy renderer rows for one entity snapshot identity. */
export function createRendererRowCache(
  snapshots: EntitySnapshotMaps | null | undefined,
  onBuild?: RendererRowBuildObserver,
): { readonly rowFor: (item: ChatItem | undefined) => MessageListRendererRow | undefined } {
  const rows = new WeakMap<ChatItem, MessageListRendererRow | undefined>();

  return {
    rowFor(item) {
      if (!item) return undefined;
      if (rows.has(item)) return rows.get(item);

      const invalidMessage = isInvalidEchoMessage(item);
      if (!invalidMessage) onBuild?.(item);
      const row = invalidMessage ? undefined : rendererRowForItem(item, snapshots);
      rows.set(item, row);
      return row;
    }
  };
}

/** Builds the legacy MessageListItem row shape from an engine item and current entity snapshots. */
export function rendererRowForItem(
  item: ChatItem | undefined,
  snapshots: EntitySnapshotMaps | null | undefined,
): MessageListRendererRow | undefined {
  if (!item) return undefined;

  const base = isRecord(item.rendererRow) ? cloneRecord(item.rendererRow) : fallbackRendererRow(item);
  const authorId = stringField(base, 'author_id') ?? item.author.id;
  const authorType = stringField(base, 'author_type') ?? item.author.type ?? 'profile';
  const author = (authorType === 'profile' ? profileSnapshot(snapshots, authorId) : null) ?? base.author ?? null;
  const placeholderAuthorFlag = item.flags?.includes(ECHO_PLACEHOLDER_AUTHOR_FLAG) ?? false;
  const missingProfileAuthor =
    authorType === 'profile' && author === null && (placeholderAuthorFlag || base.author === null || base.author === undefined);
  const text = stringField(base, 'text') ?? item.bodyHtml ?? item.body;

  return {
    ...base,
    id: base.id ?? item.id,
    sort_order: base.sort_order ?? item.sortOrder,
    timestamp: base.timestamp ?? timestampSeconds(item.createdAt),
    author_id: authorId,
    author_type: authorType,
    // Author-scoped: a row-level `pseudotype: 'skeleton'` would also skeleton the
    // present message body/attachments and break grouping in Message.jsx.
    ...(missingProfileAuthor ? { author_pseudotype: 'skeleton' } : {}),
    text,
    author,
    reactions: enrichRendererReactions(base.reactions, snapshots, []),
    entity_snapshots: mergeEntitySnapshots(base.entity_snapshots, richdownEntitySnapshots(text, snapshots)),
    groupedWithPrev: item.groupedWithPrev === true,
  };
}

function mergeEntitySnapshots(
  base: unknown,
  live: Record<string, Record<string, unknown | null>>,
): Record<string, Record<string, unknown | null>> {
  const merged: Record<string, Record<string, unknown | null>> = {};
  if (isRecord(base)) {
    for (const [type, snapshots] of Object.entries(base)) {
      if (isRecord(snapshots)) merged[type] = { ...snapshots };
    }
  }
  for (const [type, snapshots] of Object.entries(live)) {
    merged[type] = { ...merged[type], ...snapshots };
  }
  return merged;
}

/** Compares two legacy timestamp values by viewer-local calendar day. */
export function areRendererRowsDifferentDays(timestamp1: unknown, timestamp2: unknown, formatter: Intl.DateTimeFormat): boolean {
  const left = timestampNumber(timestamp1);
  const right = timestampNumber(timestamp2);
  if (left === null || right === null) return false;
  return formatter.format(new Date(left * 1000)) !== formatter.format(new Date(right * 1000));
}

function fallbackRendererRow(item: ChatItem): MessageListRendererRow {
  return {
    id: item.id,
    sort_order: item.sortOrder,
    timestamp: timestampSeconds(item.createdAt),
    author_id: item.author.id,
    author_type: 'profile',
    text: item.bodyHtml ?? item.body,
    flags: item.flags ?? [],
    attachments: [],
    link_previews: [],
    contains_only_attachments: item.containsOnlyAttachments ?? false,
    contains_only_entities: item.containsOnlyEntities ?? false,
    contains_only_emoji_entities: item.containsOnlyEmojiEntities ?? false,
    reactions: [],
  };
}

function timestampSeconds(createdAt: string): number | undefined {
  const milliseconds = Date.parse(createdAt);
  return Number.isFinite(milliseconds) ? Math.floor(milliseconds / 1000) : undefined;
}

function timestampNumber(value: unknown): number | null {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}
